import { Box, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  bookingsApiSlice,
  useLazyGetUserOrderQuery,
} from "../../api/bookings/bookingsApiSlice";
import { locationsApiSlice } from "../../api/locations/locationsApiSlice";
import { useStartRental } from "../../redux/flows/startRentalFlow";
import {
  setBookingSummary,
  setIsFirstTimeUsingSelectedLocker,
  setIsPaymentVerified,
  setIsSelectedBookingHostKeyRetrieval,
} from "../../redux/slice/booking/bookingSlice";
import {
  checkIfOrderCanBeStarted,
  getBookingStatusType,
  isEmptyObject,
} from "../../util";
import {
  setHasSelectedLocation,
  setSelectedLocation,
} from "../../redux/slice/bookingCreation/bookingCreationSlice";
import { enqueueNotification } from "../../redux/slice/notification/notificationSlice";
import { baseStore, RootState } from "../../redux/store";
import routes from "../../routes/routes";
import KolulaStorageManager from "../../storage/KolulaStorageManager";
import {
  BookingSummary as BookingSummaryProps,
  Item,
} from "../../types/bookingCreation";
import { Reservation, ServiceTypeSelectorOption } from "../../types/order";
import { transformReservations } from "../../util";
import BookingSummary from "../bookLocation/common/components/BookingSummary/BookingSummary";
import BookingSummaryNote from "../bookLocation/common/components/BookingSummary/BookingSummaryNote";
import CustomButton from "../layout/components/CustomButton";
import Header from "../layout/components/Header";
import LoaderOverlay from "../layout/components/LoaderOverlay";
import TenantLogo from "../layout/components/TenantLogo";
import SelectLocationDialog from "../locationSelection/SelectLocationDialog";
import ServiceTypeSelector from "./components/ServiceTypeSelector";
import BookLocationHeader from "../bookLocation/common/util/BookLocationHeader";
import { i18n } from "../../i18n/i18n";

const styles = {
  outerBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  innerBox: {
    maxWidth: 1000,
    margin: 0,
    padding: "0 20px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  logo: {
    width: "200px",
    marginTop: "40px",
    marginBottom: "40px",
    // Use the theme's breakpoints
    "@media (min-width:1200px)": {
      width: "300px",
    },
  },
  heading: {
    lineHeight: "28px",
    letterSpacing: "2px",
    fontWeight: "600",
  },
  buttonWrapper: {
    height: "100%",
    width: "100%",
    border: "1px solid red",
  },
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [initialServiceSelected, setInitialServiceSelected] =
    useState<ServiceTypeSelectorOption>();
  let { selectedBooking, bookingSummary } = useSelector(
    (state: RootState) => state.booking
  );
  const [searchParams] = useSearchParams();
  const { selectedServiceType } = useSelector(
    (state: RootState) => state.booking
  );
  const rentalExpired = useSelector(
    (state: RootState) => state.booking.rentalExpired
  );
  const gracePeriodMinutes = useSelector(
    (state: RootState) => state.tenantConfig.config.gracePeriodMinutes
  );
  const [filteredBookingSummary, setFilteredBookingSummary] = useState<
    BookingSummaryProps[]
  >([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [openSelectLocationDialog, setOpenSelectLocationDialog] =
    React.useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();

  const { rentalFinished, rentalStarted, itemState } = useSelector(
    (state: RootState) => state.booking
  );
  const [trigger, { data: ordersUpdate }] = useLazyGetUserOrderQuery();

  const startRental = useStartRental(selectedBooking);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [startUsed, setStartUsed] = React.useState<boolean>(false);

  const query = new URLSearchParams(useLocation().search);
  const token = query.get("token");

  const handleRetrieveBookingDetails = () => {
    navigate(routes.retrieveBookingEmailPath);
  };

  const indexDBStore = useMemo(() => new KolulaStorageManager(), []);

  function sortOrders(orders: BookingSummaryProps[]): BookingSummaryProps[] {
    return [...orders].sort((a, b) => {
      const getStatus = (order: BookingSummaryProps) => {
        const item = order.items[0];
        if (item.rental?.startedAtDate && !item.rental?.finishedAtDate) {
          return 1; // Started but not finished
        } else if (!item.rental?.startedAtDate) {
          return 2; // Not started
        } else {
          return 3; // Finished or expired
        }
      };

      const statusA = getStatus(a);
      const statusB = getStatus(b);

      if (statusA !== statusB) return statusA - statusB;

      if (statusA === 1) {
        return (
          new Date(a.items[0].toDate).getTime() -
          new Date(b.items[0].toDate).getTime()
        );
      }
      if (statusA === 2) {
        return (
          new Date(a.items[0].fromDate).getTime() -
          new Date(b.items[0].fromDate).getTime()
        );
      }
      if (statusA === 3) {
        const dateA =
          a.items[0].rental?.finishedAtDate ||
          a.items[0].rental?.expirationDate;
        const dateB =
          b.items[0].rental?.finishedAtDate ||
          b.items[0].rental?.expirationDate;

        return new Date(dateA).getTime() - new Date(dateB).getTime();
      }

      return 0;
    });
  }

  const filterAndSortBookingSummary = (selectedServiceType: string) => {
    if (selectedServiceType === ServiceTypeSelectorOption.ALL) {
      const sortedOrders = sortOrders(bookingSummary);
      setFilteredBookingSummary(sortedOrders || []);
      return;
    }

    const filteredOrders = bookingSummary.filter(
      (booking: any) => booking.service === selectedServiceType
    );
    const sortedAndFilteredOrders = sortOrders(filteredOrders);
    setFilteredBookingSummary(sortedAndFilteredOrders);
  };

  const searchOrderId = searchParams.get("order_id");

  useEffect(() => {
    dispatch(setIsPaymentVerified(null));
  });

  useEffect(() => {
    const fetchOrders = async () => {
      setLoadingOrders(true);

      const fetchSearchOrder = (await searchOrderId)
        ? (async () => {
            const result: any = await baseStore.dispatch(
              bookingsApiSlice.endpoints.getUserOrder.initiate({
                orderIds: searchOrderId,
              })
            );

            const indexDBStore = new KolulaStorageManager();
            const existingOrders = await indexDBStore.getActiveOrderIds();
            const orderId = result.data[0].id as string;

            if (existingOrders.map((orders) => orders.id).includes(orderId)) {
              return [];
            }

            if (result.data[0]) {
              await indexDBStore.insertInternalMultipleOrdersIds([orderId]);
            }

            return result?.data || [];
          })()
        : Promise.resolve([]);

      setLoadingOrders(false);

      const searchedOrders = await fetchSearchOrder;

      const combinedData = [...searchedOrders, ...(ordersUpdate || [])];

      const orderRentalStatus = combinedData.reduce(
        (acc: Record<string, boolean>, item) => {
          acc[item.id] =
            item?.items?.length > 0 && !isEmptyObject(item.items[0]?.rental);
          return acc;
        },
        {}
      );

      dispatch(setIsFirstTimeUsingSelectedLocker(orderRentalStatus));
      dispatch(setBookingSummary(combinedData));
    };

    fetchOrders();
  }, [ordersUpdate, searchOrderId]);

  useEffect(() => {
    async function getLocalOrders() {
      let retrievedOrdersDetails: Array<Reservation> | null = null;
      try {
        if (token) {
          const result = await baseStore.dispatch(
            locationsApiSlice.endpoints.getBookingDetailsByToken.initiate({
              token,
            })
          );
          if (result.error) {
            throw new Error(
              (result.error as any).data?.message ||
                getI18n().t(
                  `orders.retrieve_bookings_via_email.retrieval_error`
                )
            );
          }
          retrievedOrdersDetails = result.data;
          dispatch(
            enqueueNotification({
              severity: "success",
              message: getI18n().t(
                `orders.retrieve_bookings_via_email.success`
              ),
            })
          );
        }
      } catch (error: any) {
        dispatch(
          enqueueNotification({
            severity: "error",
            message:
              error.message ||
              getI18n().t(`orders.retrieve_bookings_via_email.error`),
          })
        );
      }

      const orderIds = await indexDBStore.getActiveOrderIds();

      const combineIds = [
        ...(orderIds || []),
        ...(retrievedOrdersDetails
          ? transformReservations(retrievedOrdersDetails)
          : []),
      ];
      const sanitizedOrderIds = combineIds.map(
        (item: { id: string }) => item.id
      );

      if (combineIds) {
        trigger({ orderIds: sanitizedOrderIds.toString() });
      }
    }

    getLocalOrders();
  }, [indexDBStore]);

  const getInitialServiceState = (bookingSummary: BookingSummaryProps[]) => {
    const serviceMapping: Record<ServiceTypeSelectorOption, string> = {
      [ServiceTypeSelectorOption.RENTAL]: "RENTAL",
      [ServiceTypeSelectorOption.APARTMENT]: "APARTMENT",
      [ServiceTypeSelectorOption.KEYS]: "KEYS",
      [ServiceTypeSelectorOption.VENUE]: "VENUE",
      [ServiceTypeSelectorOption.STORAGE]: "STORAGE",
      [ServiceTypeSelectorOption.P2P]: "P2P",
      [ServiceTypeSelectorOption.ALL]: "ALL",
      [ServiceTypeSelectorOption.PARCEL]: "PARCEL",
      [ServiceTypeSelectorOption.SUP]: "SUP",
      [ServiceTypeSelectorOption.VOUCHER]: "VOUCHER",
      [ServiceTypeSelectorOption.MORE_TO_COME]: "MORE_TO_COME",
    };
    const lastCreated = bookingSummary.reduce((latest, current) =>
      new Date(current.createdDate) > new Date(latest.createdDate)
        ? current
        : latest
    );

    return serviceMapping[lastCreated.service as ServiceTypeSelectorOption];
  };

  const handleClickOpen = () => {
    switch (selectedServiceType) {
      case ServiceTypeSelectorOption.RENTAL:
        navigate(routes.bookLocationEquipmentRentalPath);
        break;
      case ServiceTypeSelectorOption.APARTMENT:
        navigate(routes.roomRentalPath);
        break;
      case ServiceTypeSelectorOption.STORAGE:
        navigate(routes.temStorageNewBooking);
        break;
      case ServiceTypeSelectorOption.P2P:
        navigate(routes.bookLocationP2PPath);
        break;
      case ServiceTypeSelectorOption.PARCEL:
        navigate(routes.bookParcelDeliveryPath);
        break;
      case ServiceTypeSelectorOption.SUP:
        navigate(routes.bookLocationSUPPath);
        break;
      case ServiceTypeSelectorOption.KEYS:
        navigate(routes.bookLocationKeysPath);
        break;
      default:
        navigate(routes.home);
    }
  };

  const handleUsingOrder = () => {
    if (!rentalStarted) {
      return startUsingOrder();
    } else {
      if (selectedBooking) {
        return navigate(routes.makeOrderDetailsUrl(selectedBooking.id));
      }
    }
  };

  const isBookingStartDateInFuture = (order: BookingSummaryProps): boolean => {
    const now = moment();
    const startDate = moment(order.items[0].fromDate);
    return now.isBefore(startDate);
  };

  const handleClose = () => {
    dispatch(setHasSelectedLocation(false));
    dispatch(setSelectedLocation(null));
    setOpenSelectLocationDialog(false);
  };
  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen((prev) => !prev);

  const renderRightButton = () => {
    // Check if this is a key return booking
    if (selectedBooking?.service === ServiceTypeSelectorOption.KEYS) {
      const firstItem = selectedBooking.items[0];
      const itemName =
        firstItem.group?.name ||
        firstItem.group?.langNames?.[i18n.language]?.title;

      if (itemName === t("book_location.keys.summary.key_return")) {
        dispatch(setIsSelectedBookingHostKeyRetrieval(true));
        return t("book_location.keys.key_retrieval.retrieve_key");
      } else {
        dispatch(setIsSelectedBookingHostKeyRetrieval(false));
      }
    } else {
      dispatch(setIsSelectedBookingHostKeyRetrieval(false));
    }

    const actionKey =
      selectedBooking?.service === ServiceTypeSelectorOption.PARCEL
        ? "book_location.orders_page.retrieve_item"
        : !rentalStarted && !rentalFinished
        ? "book_location.orders_page.start_using"
        : "book_location.orders_page.open_order";

    return t(actionKey);
  };

  const disableButton = () => {
    console.log("rental", rentalFinished);
    if (
      rentalFinished ||
      ["item_retrieved_confirmed"].includes(itemState ?? "")
    ) {
      return true;
    }
    if (
      itemState === "item_placement_confirmed" &&
      selectedBooking &&
      (selectedBooking.service === "p2p" || selectedBooking.service === "keys")
    ) {
      return true;
    }
    // if (
    //   selectedBooking &&
    //   selectedBooking.items[0].rental.state === "item_placement_confirmed" &&
    //  selectedBooking.service === "keys"
    // ) {
    //   return true;
    // }

    if (selectedBooking) {
      if (selectedBooking.service === "parcel") {
        if (Object.keys(selectedBooking.items[0].rental).length === 0) {
          return true;
        } else if (
          selectedBooking.items[0].rental.state === "item_retrieved_confirmed"
        ) {
          return true;
        }
      }
      return !checkIfOrderCanBeStarted(selectedBooking, gracePeriodMinutes);
    }
  };

  const startUsingOrder = async () => {
    if (selectedBooking && selectedBooking.service !== "parcel") {
      setLoading(true);
      await startRental();
      setStartUsed(true);
    } else if (selectedBooking && selectedBooking.service === "parcel") {
      navigate(routes.makeOrderDetailsUrl(selectedBooking.id || ""));
    }
  };

  useEffect(() => {
    if (rentalStarted && selectedBooking && startUsed) {
      setLoading(false);
      navigate(routes.makeOrderDetailsUrl(selectedBooking.id || ""));
      setStartUsed(false);
    } else if (selectedBooking && startUsed && rentalStarted === false) {
      setStartUsed(false);
      setLoading(false);
    }
  }, [rentalStarted, startUsed]);

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (!initialServiceSelected && bookingSummary.length > 0) {
      const initialService = getInitialServiceState(
        bookingSummary
      ) as keyof typeof ServiceTypeSelectorOption;
      setInitialServiceSelected(ServiceTypeSelectorOption[initialService]);
    }

    filterAndSortBookingSummary(selectedServiceType);
  }, [bookingSummary, selectedServiceType]);



  const differentiateSameBookings = useMemo(() => {
    const alphabets = [
      "A",
      "B",
      "C",
      "D",
      "E",
      "F",
      "G",
      "H",
      "I",
      "J",
      "K",
      "L",
      "M",
      "N",
      "O",
      "P",
      "Q",
      "R",
      "S",
      "T",
      "U",
      "V",
      "W",
      "X",
      "Y",
      "Z",
    ];

    // Group by bookingNumber
    const grouped = filteredBookingSummary.reduce<Record<string, any[]>>(
      (map, booking) => {
        (map[booking.bookingNumber] ||= []).push(booking);
        return map;
      },
      {}
    );

    // Assign letters only for groups with more than 1 booking
    const bookingsWithLetters = Object.values(grouped).flatMap((group) => {
      if (group.length <= 1) {
        return group;
      }

      const groupService = group?.[0]?.service;

      if (groupService === ServiceTypeSelectorOption.KEYS) {
        return [...group]
          .sort((a, b) =>
            a?.items?.[0]?.toDate.localeCompare(b?.items?.[0]?.toDate)
          )
          .map((booking, index) => {
            const itemName =
              index === 0
                ? t("book_location.keys.summary.key_pickup")
                : t("book_location.keys.summary.key_return");

            const bookingItems = booking.items.map((item: Item) => ({
              ...item,
              group: {
                ...item.group,
                name: itemName,
                langNames: {
                  de: {
                    ...item.group?.langNames?.de,
                    title: itemName,
                  },
                  en: {
                    ...item.group?.langNames?.en,
                    title: itemName,
                  },
                },
              },
            }));
            return {
              ...booking,
              bookingNumber: `${booking.bookingNumber}${
                alphabets[index] || ""
              }`,
              items: bookingItems,
            };
          })
          .sort((a, b) => a.id.localeCompare(b.id));
      }

      return [...group]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((booking, index) => ({
          ...booking,
          bookingNumber: `${booking.bookingNumber}${alphabets[index] || ""}`,
        }));
    });
    const differentiatedAndSortedBookings = filteredBookingSummary.map(
      (orig) => bookingsWithLetters.find((b) => b.id === orig.id) || orig
    );
    return differentiatedAndSortedBookings;
  }, [filteredBookingSummary]);

  if (loadingOrders) {
    return <LoaderOverlay open={loadingOrders} />;
  }

  return (
    <Box sx={styles.outerBox}>
      <Box sx={styles.innerBox}>
        <Header>
          <TenantLogo />
        </Header>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <BookLocationHeader text={t("orders.ticket_overview.header")} />
        </Box>
        <Box>
          <ServiceTypeSelector
            initialSelected={
              initialServiceSelected ?? ServiceTypeSelectorOption.RENTAL
            }
            bookingSummary={bookingSummary || []}
          />
        </Box>
        {filteredBookingSummary?.length > 0 ? (
          <BookingSummary
            userReservation={differentiateSameBookings}
            serviceType={selectedServiceType}
          />
        ) : (
          <Typography
            variant="h5"
            sx={{
              marginTop: "52px",
              display: "grid",
              justifyItems: "center",
              marginBottom: "10px",
            }}
          >
            {t("orders.ticket_overview.no_booking")}
          </Typography>
        )}
      </Box>
      {filteredBookingSummary?.length > 0 && (
        <div className="flex flex-1 justify-center flex-col w-full px-5">
          {selectedBooking?.service ===
          ServiceTypeSelectorOption.PARCEL ? null : selectedBooking?.service ===
              ServiceTypeSelectorOption.KEYS &&
            isEmptyObject(selectedBooking.items[0]?.rental) ? (
            <BookingSummaryNote serviceType={selectedServiceType} />
          ) : selectedBooking?.service ===
              ServiceTypeSelectorOption.APARTMENT ||
            selectedBooking?.service === ServiceTypeSelectorOption.SUP ||
            (selectedBooking?.service === ServiceTypeSelectorOption.VENUE &&
              selectedBooking.items[0]?.rental) ? (
            <BookingSummaryNote serviceType={selectedServiceType} />
          ) : (
            selectedBooking &&
            isBookingStartDateInFuture(selectedBooking) &&
            getBookingStatusType(
              selectedBooking.items[0]?.rental,
              rentalExpired,
              selectedServiceType
            ) === "booked" && (
              <BookingSummaryNote serviceType={selectedServiceType} />
            )
          )}
          {selectedBooking?.service === ServiceTypeSelectorOption.VENUE || selectedBooking?.service === ServiceTypeSelectorOption.APARTMENT ? (
            <div className="mt-10" />
          ) : (
            <CustomButton
              isButtonDark={true}
              onClick={handleUsingOrder}
              disabled={disableButton()}
              children={renderRightButton()}
            />
          )}
        </div>
      )}
      <div
        className={`w-full px-5 max-w-[440px] ${
          filteredBookingSummary?.length > 0
            ? "relative mb-20 "
            : "fixed  bottom-20 "
        }`}
      >
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className="w-full font-semibold text-foreground/90 leading-[56px] text-[20px] bg-white border border-primary px-4 rounded-xl hover:bg-gray-100 flex items-center justify-center"
        >
          {t("orders.button.add")}
          <MdKeyboardArrowDown
            className={`ml-2 transform transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {open && (
          <div
            ref={menuRef}
            className="absolute left-5 right-5 bottom-full mt-2 py-3 bg-background border border-foreground/10 rounded-xl shadow-lg z-50"
          >
            <div className="hover:bg-foreground/10">
              <button
                onClick={handleClickOpen}
                className="w-full text-left px-4 py-1"
              >
                {t("orders.button.new")}
              </button>
            </div>
            <div className="hover:bg-foreground/10">
              <button
                onClick={handleRetrieveBookingDetails}
                className="w-full text-left px-4 py-1"
              >
                {t("orders.button.previous")}
              </button>
            </div>
          </div>
        )}
      </div>

      <SelectLocationDialog
        handleClickOpen={handleClickOpen}
        handleClose={handleClose}
        openSelectLocationDialog={openSelectLocationDialog}
      />
      <LoaderOverlay open={loading} />
    </Box>
  );
};

export default OrdersPage;
